import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, formatNumber, formatBytes, timeAgo, getStatusColor } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import type { Device, PaginatedResponse } from '@/types';

interface SoftwareEntry {
  name: string;
  publisher: string;
  category: string;
  versions: { version: string; deviceCount: number; devices: { hostname: string; version: string; installDate: string; sizeMb: number }[] }[];
  totalInstallCount: number;
  totalSizeMb: number;
  lastSeen: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  Browser: '#3b82f6',
  Productivity: '#22c55e',
  Communication: '#f59e0b',
  Development: '#a855f7',
  Utility: '#6b7280',
  Runtime: '#ec4899',
  System: '#06b6d4',
  Database: '#f97316',
  Network: '#8b5cf6',
  Editor: '#14b8a6',
  Document: '#eab308',
  Container: '#3b82f6',
};

export function Software() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [publisherFilter, setPublisherFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  useEffect(() => {
    mockApi.getDevices({ pageSize: 500 } as any).then((res: PaginatedResponse<Device>) => {
      const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? (res as unknown as Device[]) : []);
      setDevices(rows);
      setLoading(false);
    }).catch(() => {
      setDevices([]);
      setLoading(false);
    });
  }, []);

  const softwareList = useMemo<SoftwareEntry[]>(() => {
    const map = new Map<string, SoftwareEntry>();
    for (const device of devices ?? []) {
      for (const sw of device.installedSoftware ?? []) {
        if (!map.has(sw.name)) {
          map.set(sw.name, {
            name: sw.name,
            publisher: sw.publisher,
            category: sw.category,
            versions: [],
            totalInstallCount: 0,
            totalSizeMb: 0,
            lastSeen: sw.installDate,
          });
        }
        const entry = map.get(sw.name)!;
        entry.totalInstallCount++;
        entry.totalSizeMb += sw.sizeMb;

        let versionGroup = entry.versions.find(v => v.version === sw.version);
        if (!versionGroup) {
          versionGroup = { version: sw.version, deviceCount: 0, devices: [] };
          entry.versions.push(versionGroup);
        }
        versionGroup.deviceCount++;
        versionGroup.devices.push({
          hostname: device.hostname,
          version: sw.version,
          installDate: sw.installDate,
          sizeMb: sw.sizeMb,
        });

        if (new Date(sw.installDate) > new Date(entry.lastSeen)) {
          entry.lastSeen = sw.installDate;
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => b.totalInstallCount - a.totalInstallCount);
  }, [devices]);

  const categories = useMemo(() => {
    return Array.from(new Set(softwareList.map(s => s.category))).sort();
  }, [softwareList]);

  const publishers = useMemo(() => {
    return Array.from(new Set(softwareList.map(s => s.publisher))).sort();
  }, [softwareList]);

  const filtered = useMemo(() => {
    return softwareList.filter(s => {
      if (search && !s.name.toLowerCase().includes(search.toLowerCase()) && !s.publisher.toLowerCase().includes(search.toLowerCase())) return false;
      if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
      if (publisherFilter !== 'all' && s.publisher !== publisherFilter) return false;
      return true;
    });
  }, [softwareList, search, categoryFilter, publisherFilter]);

  const categoryStats = useMemo(() => {
    const stats: Record<string, number> = {};
    for (const s of softwareList) {
      stats[s.category] = (stats[s.category] || 0) + 1;
    }
    return Object.entries(stats).sort((a, b) => b[1] - a[1]);
  }, [softwareList]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[var(--text-muted)] text-sm">Loading software inventory…</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Software Inventory</h1>
          <p className="text-sm text-[var(--text-muted)] mt-0.5">{softwareList.length} unique packages across {devices.length} devices</p>
        </div>
        <Button variant="outline" onClick={() => showToast('Software report exported')}>
          Export
        </Button>
      </div>

      {/* Category cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {categoryStats.slice(0, 6).map(([cat, count]) => (
          <Card key={cat} className="p-3 cursor-pointer hover:border-[var(--border-strong)]" onClick={() => setCategoryFilter(cat === categoryFilter ? 'all' : cat)}>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: CATEGORY_COLORS[cat] || 'var(--accent)' }} />
              <span className="text-xs text-[var(--text-muted)]">{cat}</span>
            </div>
            <p className="text-lg font-semibold text-[var(--text-primary)] mt-1 tabular-nums">{count}</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search software or publisher…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)]"
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
        >
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={publisherFilter}
          onChange={(e) => setPublisherFilter(e.target.value)}
          className="bg-[var(--panel)] border border-[var(--border)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)]"
        >
          <option value="all">All Publishers</option>
          {publishers.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--panel)] border border-[var(--border)] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)] text-xs text-[var(--text-muted)] uppercase tracking-wider">
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Publisher</th>
              <th className="text-left px-4 py-3 font-medium">Category</th>
              <th className="text-right px-4 py-3 font-medium">Versions</th>
              <th className="text-right px-4 py-3 font-medium">Installs</th>
              <th className="text-right px-4 py-3 font-medium">Total Size</th>
              <th className="text-left px-4 py-3 font-medium">Last Seen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((sw) => (
              <>
                <tr
                  key={sw.name}
                  className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover)] cursor-pointer"
                  onClick={() => setExpandedRow(expandedRow === sw.name ? null : sw.name)}
                >
                  <td className="px-4 py-3 text-sm text-[var(--text-primary)] font-medium">{sw.name}</td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">{sw.publisher}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY_COLORS[sw.category] || 'var(--accent)' }} />
                      <span className="text-[var(--text-secondary)]">{sw.category}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)] text-right tabular-nums">{sw.versions.length}</td>
                  <td className="px-4 py-3 text-sm text-[var(--text-primary)] text-right tabular-nums font-medium">{sw.totalInstallCount}</td>
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)] text-right tabular-nums">{formatBytes(sw.totalSizeMb * 1024 * 1024)}</td>
                  <td className="px-4 py-3 text-sm text-[var(--text-muted)]">{timeAgo(sw.lastSeen)}</td>
                </tr>
                {expandedRow === sw.name && (
                  <tr className="bg-[var(--elevated)]">
                    <td colSpan={7} className="px-4 py-4">
                      <div className="space-y-3">
                        <h4 className="text-xs text-[var(--text-muted)] uppercase tracking-wider font-medium">Installed on {sw.totalInstallCount} devices</h4>
                        <table className="w-full">
                          <thead>
                            <tr className="text-xs text-[var(--text-muted)]">
                              <th className="text-left py-2 font-medium">Hostname</th>
                              <th className="text-left py-2 font-medium">Version</th>
                              <th className="text-left py-2 font-medium">Install Date</th>
                              <th className="text-right py-2 font-medium">Size</th>
                              <th className="text-right py-2 font-medium"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {sw.versions.flatMap(v => v.devices).slice(0, 20).map((d, i) => (
                              <tr key={i} className="text-sm">
                                <td className="py-1.5 text-[var(--text-primary)] hover:text-[var(--accent)] cursor-pointer" onClick={() => navigate('/devices')}>{d.hostname}</td>
                                <td className="py-1.5 text-[var(--text-secondary)]">{d.version}</td>
                                <td className="py-1.5 text-[var(--text-muted)]">{timeAgo(d.installDate)}</td>
                                <td className="py-1.5 text-right text-[var(--text-secondary)] tabular-nums">{formatBytes(d.sizeMb * 1024 * 1024)}</td>
                                <td className="py-1.5 text-right">
                                  <span className="text-[var(--accent)] text-xs cursor-pointer hover:underline" onClick={() => navigate('/devices')}>View device →</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {sw.totalInstallCount > 20 && (
                          <p className="text-xs text-[var(--text-muted)]">Showing 20 of {sw.totalInstallCount} installations</p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-[var(--text-muted)]">No software found matching your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function showToast(msg: string) {
  const event = new CustomEvent('toast', { detail: { message: msg, type: 'success' } });
  window.dispatchEvent(event);
}
